import DashedBorder from "@/components/ui/DashedBorder";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import React, { useRef, useEffect } from "react";
import { useActiveCanvas } from "@/utils/utils";
import { ItemRect } from "./ResizableOutline";
import { AITextEditButton } from "../Canvas/AITextEditButton";

export enum OutlineType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  CHILD = "child",
  MOVE = "move",
}

export interface Palette {
  primary: string;
  component: string;
}

export interface ElementOutlineProps {
  type: OutlineType;

  itemRect: ItemRect;

  selected?: boolean;
  tagName?: string;
  isComponent?: boolean;

  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

export const ELEMENT_OUTLINE_COLOR = "#4597F7";
export const COMPONENT_OUTLINE_COLOR = "#6183e4";

const DASHED_THICKNESS = 2.5;
const DASH_LENGTH = 1;
const DASHED_GAP = 0.75;

// For the solid outlines
const OUTLINE_THICKNESS = 0.75;

const getColours = (): Palette => {
  return {
    primary: ELEMENT_OUTLINE_COLOR,
    component: COMPONENT_OUTLINE_COLOR,
  };
};

const getZIndexForType = (type: OutlineType): number => {
  switch (type) {
    case OutlineType.PRIMARY:
      return 3;
    case OutlineType.SECONDARY:
      return 2;
    case OutlineType.CHILD:
      return 1;
    case OutlineType.MOVE:
      return 4;
    default:
      return 1;
  }
};

const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export interface ElementOutlineRef {
  instantUpdateWithRect: (rect: ItemRect) => void;
}

const ElementOutline = React.forwardRef<ElementOutlineRef, ElementOutlineProps>(
  (
    {
      type,
      itemRect,
      selected = false,
      tagName,
      isComponent = false,
      iframeRef,
    },
    ref,
  ) => {
    const palette = getColours();

    const color = isComponent ? palette.component : palette.primary;
    const zIndex = getZIndexForType(type);

    const outlineRef = useRef<HTMLDivElement>(null);
    const primaryOutlineRef = useRef<HTMLDivElement>(null);
    const sizeDisplayRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        instantUpdateWithRect: (rect: ItemRect) => {
          if (outlineRef.current) {
            outlineRef.current.style.left = `${rect.x}px`;
            outlineRef.current.style.top = `${rect.y}px`;
            outlineRef.current.style.width = `${rect.width}px`;
            outlineRef.current.style.height = `${rect.height}px`;
          }

          if (primaryOutlineRef.current) {
            primaryOutlineRef.current.style.left = `${rect.x}px`;
            primaryOutlineRef.current.style.top = `${rect.y}px`;
            primaryOutlineRef.current.style.width = `${rect.width}px`;
            primaryOutlineRef.current.style.height = `${rect.height}px`;
          }

          if (sizeDisplayRef.current) {
            sizeDisplayRef.current.innerText = `${Math.round(
              rect.width,
            )} x ${Math.round(rect.height)}`;
          }

          if (controlsRef.current) {
            controlsRef.current.style.top = `${rect.height + 10}px`;
          }
        },
      }),
      [],
    );

    const unzoomMultiplierRef = useRef(1);
    const activeCanvas = useActiveCanvas();

    // Subscribe to realtime zoom and update CSS variable imperatively (no React re-renders)
    useEffect(() => {
      const updateCssVariable = () => {
        const canvasId = activeCanvas?.id;
        if (!canvasId || !outlineRef.current) return;

        const zoomPerc =
          useCanvasStore.getState().canvasIdToPosRealtime[canvasId]?.zoomPerc ||
          1;
        const multiplier = 1 / zoomPerc;
        unzoomMultiplierRef.current = multiplier;
        outlineRef.current.style.setProperty(
          "--unzoom-multiplier",
          String(multiplier),
        );
      };

      // Initial update
      updateCssVariable();

      // Subscribe to store changes
      const unsubscribe = useCanvasStore.subscribe(updateCssVariable);
      return () => unsubscribe();
    }, [activeCanvas?.id]);

    if (!itemRect) {
      return null;
    }

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      pointerEvents: "none",
      zIndex,
      boxSizing: "border-box",
    };

    baseStyle.left = `${itemRect.x}px`;
    baseStyle.top = `${itemRect.y}px`;
    baseStyle.width = `${itemRect.width}px`;
    baseStyle.height = `${itemRect.height}px`;

    const getOutline = (): React.ReactNode => {
      if (type === OutlineType.CHILD || type === OutlineType.MOVE) {
        // DashedBorder uses SVG data URL so can't use CSS calc() - use ref value
        // Slight lag during zoom is acceptable for dashed borders
        return (
          <DashedBorder
            dashLength={Math.round(DASH_LENGTH * unzoomMultiplierRef.current)}
            gapLength={Math.round(DASHED_GAP * unzoomMultiplierRef.current)}
            borderWidth={Math.round(
              DASHED_THICKNESS * unzoomMultiplierRef.current,
            )}
            borderColor={color}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        );
      } else {
        // Use CSS calc() with --unzoom-multiplier for smooth updates without re-renders
        return (
          <div
            ref={primaryOutlineRef}
            style={{
              width: `${itemRect.width}px`,
              height: `${itemRect.height}px`,
              outline: `calc(var(--unzoom-multiplier, 1) * ${OUTLINE_THICKNESS}px) solid ${color}`,
              border: `calc(var(--unzoom-multiplier, 1) * ${OUTLINE_THICKNESS}px) solid ${color}`,
              boxSizing: "border-box",
            }}
          />
        );
      }
    };

    const renderControls = () => {
      if (!selected || type !== OutlineType.PRIMARY) {
        return null;
      }

      const controlsStyle: React.CSSProperties = {
        position: "absolute",
        top: `${itemRect.height + 10}px`,
        left: `${0}px`,
        zIndex: zIndex + 1,
        pointerEvents: "auto",
        display: "flex",
        gap: "4px",
      };

      return (
        <div ref={controlsRef} style={controlsStyle}>
          {/* AI Text Edit Button */}
          <AITextEditButton color={color} iframeRef={iframeRef} />

          {/* Size display */}
          <div
            style={{
              backgroundColor: color,
              color: "white",
              padding: "2px 6px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {Math.round(itemRect.width)} x {Math.round(itemRect.height)}
          </div>

          {/* Tag name */}
          {tagName && (
            <div
              style={{
                backgroundColor: color,
                color: "white",
                padding: "2px 6px",
                borderRadius: "3px",
                fontSize: "11px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              {isComponent
                ? capitalizeFirstLetter(tagName)
                : tagName.toLowerCase()}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="relative" ref={outlineRef} style={baseStyle}>
        {getOutline()}
        {renderControls()}
      </div>
    );
  },
);

export default ElementOutline;
